import { Helmet } from "react-helmet-async";

const BASE = "https://hacking.community";

/**
 * Per-page SEO head — title, description, canonical, og:*, twitter:*, and JSON-LD.
 * Drops into any route. Falls back gracefully when a field is missing.
 *
 *   <SEOHead
 *       title="..."
 *       description="..."
 *       path="/q/abc123"           // canonical path (no host)
 *       image="https://.../og.png" // optional override; default brand card otherwise
 *       type="article"             // "website" | "article" | "profile"
 *       jsonLd={{...}}             // optional JSON-LD object to inject
 *       noindex={false}            // set true on private/duplicate pages
 *   />
 */
export default function SEOHead({
    title,
    description,
    path,
    image,
    type = "website",
    jsonLd,
    keywords,
    noindex = false,
    noCanonical = false,
}) {
    // Suffix only when the result stays under ~62 chars (2026-09 audit: 424 long titles).
    const fullTitle = !title ? "hacking.community" : (title.includes("hacking.community") || title.length > 42 ? title : `${title} · hacking.community`);
    const url = path ? `${BASE}${path}` : BASE;
    const ogImage = image || `${BASE}/api/og/default.png`;
    // Meta descriptions truncate around 160 chars in SERPs — cut at a word boundary.
    let desc = (description || "").trim();
    if (desc.length > 160) {
        const cut = desc.slice(0, 157);
        desc = `${cut.slice(0, cut.lastIndexOf(" ") > 100 ? cut.lastIndexOf(" ") : 157)}…`;
    }
    const kw = (keywords || "").trim();

    return (
        <Helmet>
            <title>{fullTitle}</title>
            {desc && <meta name="description" content={desc} />}
            {kw && <meta name="keywords" content={kw} />}
            {!noCanonical && <link rel="canonical" href={url} />}
            <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1"} />
            <meta name="author" content="HACKASTRA INFOSEC L.L.C-FZ" />
            <meta name="publisher" content="HACKASTRA INFOSEC L.L.C-FZ" />

            <meta property="og:type" content={type} />
            <meta property="og:site_name" content="hacking.community" />
            <meta property="og:title" content={title || "hacking.community"} />
            {desc && <meta property="og:description" content={desc} />}
            <meta property="og:url" content={url} />
            <meta property="og:image" content={ogImage} />
            <meta property="og:image:alt" content={title || "hacking.community"} />
            <meta property="og:locale" content="en_US" />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:site" content="@hack__community" />
            <meta name="twitter:creator" content="@hack__community" />
            <meta name="twitter:title" content={title || "hacking.community"} />
            {desc && <meta name="twitter:description" content={desc} />}
            <meta name="twitter:image" content={ogImage} />
            <meta name="twitter:image:alt" content={title || "hacking.community"} />

            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}

/** Strip markdown and excess whitespace from a body so it makes a clean description. */
export function makeDescription(body, max = 200) {
    if (!body) return "";
    // Drop fenced code blocks (often dominate the body) + inline code + markdown chrome.
    const clean = body
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`[^`]*`/g, " ")
        .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/[#>*_~]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return clean.length > max ? clean.slice(0, max - 1).trimEnd() + "…" : clean;
}

/**
 * Build a BreadcrumbList JSON-LD object that Lighthouse + Google look for.
 * `crumbs` is an array of `{ name, path }`. The final crumb omits its `path`
 * (it's the current page).
 *
 *   buildBreadcrumbSchema([
 *     { name: "Jobs", path: "/jobs" },
 *     { name: "Senior SecEng @ ACME" },
 *   ])
 */
export function buildBreadcrumbSchema(crumbs) {
    if (!Array.isArray(crumbs) || crumbs.length === 0) return null;
    const BASE = "https://hacking.community";
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": crumbs.map((c, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": c.name,
            ...(c.path ? { "item": `${BASE}${c.path}` } : {}),
        })),
    };
}

/**
 * Event JSON-LD for a scheduled multiplayer tournament — Google Events eligible
 * (online event: attendance mode + VirtualLocation are the required signals).
 */
export function buildEventSchema(t) {
    if (!t?.scheduled_start) return null;
    const start = new Date(t.scheduled_start);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getTime() + (t.duration_minutes || 60) * 60000);
    return {
        "@context": "https://schema.org",
        "@type": "Event",
        name: t.title,
        description: (t.description || `Multiplayer CTF tournament on hacking.community${t.lab_title ? ` — ${t.lab_title}` : ""}.`).slice(0, 300),
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
        eventStatus: t.status === "cancelled" ? "https://schema.org/EventCancelled" : "https://schema.org/EventScheduled",
        location: { "@type": "VirtualLocation", url: "https://hacking.community/tournaments" },
        organizer: { "@type": "Organization", name: "hacking.community", url: "https://hacking.community" },
        image: ["https://hacking.community/api/og/default.png"],
        isAccessibleForFree: true,
        offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://hacking.community/tournaments",
            validFrom: start.toISOString(),
        },
    };
}

/**
 * Merge multiple JSON-LD schemas into a single `@graph` so we still ship one
 * `<script>` tag (Helmet limitation w/ multiple raw scripts) but expose every
 * entity type Lighthouse + Google look for (Article + Breadcrumb + …).
 */
export function mergeSchemas(...schemas) {
    const filtered = schemas.filter(Boolean);
    if (filtered.length === 0) return null;
    if (filtered.length === 1) return filtered[0];
    return { "@context": "https://schema.org", "@graph": filtered };
}
